import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isEmpty } from "lodash";
import styled from "styled-components";
import { Button, Card, Rate, Spin, Typography } from "antd";
import * as facilityApi from "./api";
import {
  isHttpError,
  RatingAggregatesAttributes,
  RatingBreakdownItem,
  WorkplaceComment,
} from "./api";

const { Text, Title } = Typography;

const Scroll = styled.div`
  max-height: 400px;
  overflow-y: scroll;
`;

const AggregateSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 16px;
`;

const AggregateLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
`;

const BreakdownSection = styled.div`
  flex: 1;
`;

const BreakdownRow = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 2px 4px;
  border: none;
  border-radius: 4px;
  width: 100%;
  font: inherit;
  background: ${({ $active }) => ($active ? "#e6f7ff" : "transparent")};
  &:hover {
    background: ${({ $active }) => ($active ? "#bae7ff" : "#f5f5f5")};
  }
`;

const BarBackground = styled.div`
  flex: 1;
  height: 12px;
  background: #f0f0f0;
  border-radius: 6px;
  overflow: hidden;
`;

const BarFill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${({ $percent }) => $percent}%;
  background: #faad14;
  border-radius: 6px;
  transition: width 0.3s;
`;

const ReviewCardContainer = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
`;

const ReviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const PaginationSection = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 12px;
`;

interface FacilityReviewCardProps {
  facilityId?: string;
}

function RatingBreakdownChart({
  breakdown,
  total,
  activeRating,
  onSelect,
}: {
  breakdown: RatingBreakdownItem[];
  total: number;
  activeRating: number | undefined;
  onSelect: (rating: number | undefined) => void;
}) {
  const breakdownMap = new Map(breakdown.map((b) => [b.rating, b.numberOfRatings]));

  return (
    <BreakdownSection>
      {([5, 4, 3, 2, 1] as const).map((star) => {
        const count = breakdownMap.get(star) ?? 0;
        const percent = total > 0 ? (count / total) * 100 : 0;
        const isActive = activeRating === star;
        return (
          <BreakdownRow
            key={star}
            type="button"
            $active={isActive}
            onClick={() => onSelect(isActive ? undefined : star)}
          >
            <Text style={{ width: 14, textAlign: "right", fontSize: 12 }}>{star}</Text>
            <Rate disabled value={1} count={1} style={{ fontSize: 12 }} />
            <BarBackground>
              <BarFill $percent={percent} />
            </BarBackground>
            <Text style={{ width: 28, textAlign: "right", fontSize: 12 }}>{count}</Text>
          </BreakdownRow>
        );
      })}
    </BreakdownSection>
  );
}

function FacilityReviewCard({ facilityId }: FacilityReviewCardProps) {
  const requestIdRef = useRef(0);
  const [comments, setComments] = useState<WorkplaceComment[]>([]);
  const [aggregates, setAggregates] = useState<RatingAggregatesAttributes | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [ratingFilter, setRatingFilter] = useState<number | undefined>(undefined);
  const [nextPageUrl, setNextPageUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    async function fetchData() {
      if (!facilityId) {
        setAggregates(undefined);
        setComments([]);
        setNextPageUrl(undefined);
        setRatingFilter(undefined);
        setError(false);
        setLoading(false);
        setLoadingMore(false);
        return;
      }
      setLoading(true);
      setError(false);
      try {
        const [aggregatesResult, commentsResult] = await Promise.allSettled([
          facilityApi.fetchWorkplaceRatingAggregates(facilityId),
          facilityApi.fetchWorkplaceComments(facilityId),
        ]);
        if (requestId !== requestIdRef.current) {
          return;
        }

        const hasNon404Error = [aggregatesResult, commentsResult].some(
          (r) => r.status === "rejected" && !(isHttpError(r.reason) && r.reason.status === 404)
        );

        if (hasNon404Error) {
          setError(true);
          setLoadingMore(false);
          return;
        }

        const aggr =
          aggregatesResult.status === "fulfilled"
            ? aggregatesResult.value.data.attributes
            : undefined;
        const cmts =
          commentsResult.status === "fulfilled" ? commentsResult.value : { data: [], links: {} };

        setAggregates(aggr);
        setComments(cmts.data);
        setNextPageUrl(cmts.links.next);
      } catch {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setError(true);
        setLoadingMore(false);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }
    void fetchData();
  }, [facilityId]);

  const loadMore = useCallback(async () => {
    if (!facilityId || !nextPageUrl) {
      return;
    }
    const requestId = requestIdRef.current;
    setLoadingMore(true);
    try {
      const commentsResp = await facilityApi.fetchWorkplaceComments(facilityId, nextPageUrl);
      if (requestId !== requestIdRef.current) {
        return;
      }
      setComments((prev) => [...prev, ...commentsResp.data]);
      setNextPageUrl(commentsResp.links.next);
    } catch {
      // Silently fail on load more — existing data remains visible
    } finally {
      if (requestId === requestIdRef.current) {
        setLoadingMore(false);
      }
    }
  }, [facilityId, nextPageUrl]);

  const filteredComments = useMemo(() => {
    const sorted = comments.toSorted(
      (a, b) =>
        new Date(b.attributes.createdAt).getTime() - new Date(a.attributes.createdAt).getTime()
    );
    return ratingFilter
      ? sorted.filter((comment) => comment.attributes.rating === ratingFilter)
      : sorted;
  }, [comments, ratingFilter]);

  if (loading) {
    return (
      <Card title="Reviews">
        <Spin />
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="Reviews">
        <Text type="secondary">Unable to load reviews</Text>
      </Card>
    );
  }

  const hasNoReviews = !aggregates && isEmpty(comments);
  if (hasNoReviews) {
    return (
      <Card title="Reviews">
        <Text type="secondary">No reviews yet</Text>
      </Card>
    );
  }

  return (
    <Card title="Reviews">
      {aggregates && aggregates.numberOfRatings > 0 && (
        <AggregateSection>
          <AggregateLeft>
            <Title level={2} style={{ margin: 0 }}>
              {aggregates.avgRating.toFixed(1)}
            </Title>
            <Rate disabled allowHalf value={aggregates.avgRating} style={{ fontSize: 14 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {aggregates.numberOfRatings} ratings
            </Text>
          </AggregateLeft>
          <RatingBreakdownChart
            breakdown={aggregates.ratingsBreakdown}
            total={aggregates.numberOfRatings}
            activeRating={ratingFilter}
            onSelect={setRatingFilter}
          />
        </AggregateSection>
      )}

      {ratingFilter && (
        <div style={{ marginBottom: 8 }}>
          <Button size="small" onClick={() => setRatingFilter(undefined)}>
            Clear filter: {ratingFilter} star{ratingFilter !== 1 ? "s" : ""}
          </Button>
        </div>
      )}

      <Scroll>
        {isEmpty(filteredComments) ? (
          <Text type="secondary">{ratingFilter ? "No reviews for this rating" : "No reviews"}</Text>
        ) : (
          filteredComments.map((comment) => (
            <ReviewCardContainer key={comment.id}>
              <ReviewHeader>
                {comment.attributes.workerInfo.id ? (
                  <a
                    href={`${process.env.REACT_APP_BASE_PATH || "/"}agent/dashboard?userId=${comment.attributes.workerInfo.id}&drawer=Agent`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Text strong style={{ color: "inherit" }}>
                      {comment.attributes.workerInfo.name}
                    </Text>
                  </a>
                ) : (
                  <Text strong>{comment.attributes.workerInfo.name}</Text>
                )}
                <Text type="secondary">
                  {new Date(comment.attributes.createdAt).toLocaleDateString()}
                </Text>
              </ReviewHeader>
              {comment.attributes.rating !== undefined && (
                <Rate disabled value={comment.attributes.rating} style={{ fontSize: 14 }} />
              )}
              <div style={{ marginTop: 4 }}>
                <Text>{comment.attributes.text}</Text>
              </div>
            </ReviewCardContainer>
          ))
        )}
      </Scroll>

      {nextPageUrl && (
        <PaginationSection>
          <Button type="link" loading={loadingMore} onClick={() => void loadMore()}>
            Load More Reviews
          </Button>
        </PaginationSection>
      )}
    </Card>
  );
}

export { FacilityReviewCard };
